import { Invitation, Profile } from '@playcastdotio/Messaging';
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faChevronLeft,
  faGear,
  faLink,
  faPencil,
  faPlay,
  faSpinner,
  faUser,
  faPlus,
  faCheck,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { isMobileDevice, sanitizeUrl } from '../utility';
import { Link } from 'react-router-dom';
import { BannerSelectionModal, AliasSelectionModal, SocialLinksModal, LauncherModal } from './modals';
import { SOCIAL_PLATFORMS } from './constants';
import { IdentityPlatform, InvitationSendState, PlayerJoinState } from '@playcastdotio/CoreTypes';
import { Input } from '@playcastdotio/Shadcn';
import { SignedOut, SignInButton } from '@clerk/clerk-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@playcastdotio/Shadcn';
import clsx from 'clsx';
import { sendAnalytics, PlaycastAnalyticsLobbyCreationInitialized, PlaycastAnalyticsLobbyCardOpened } from '@playcastdotio/Analytics';

// Add CSS to hide scrollbars
const hideScrollbarStyles = `
  .hide-scrollbar::-webkit-scrollbar {
    display: none;
  }
  .hide-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

// Lobby creation limits
const LIMIT_LOBBY_CREATION = true;
const MAX_OWNED_LOBBIES = 1;

export type ProfileState = {
  invitations: Invitation[];
  profile: Profile | null;
  isLoading: boolean;
  error: string | null;
  nextCursor?: string;
  lastInvalidatedInvitationId?: string;
  invitationsLoading?: boolean;
  invitationsError?: string;
  selectedInvitationId?: string;
  inQueue?: string;
  matchFoundId?: string;
  matchBroadcastId?: string;
  creating?: boolean;
  isOwner: boolean;
  noHostsAvailable?: boolean; // Set when MATCHMAKER_QUEUE_STATUS received with noHostsAvailable flag
  matchFailed?: { reason: string; canRetry: boolean };
  queuePosition?: number; // Set from MATCHMAKING_V2_QUEUE_STATUS position
  estimatedSetupTimeSeconds?: number; // Set from MATCHMAKING_V2_MATCH_FOUND, when provided
  matchEnded?: { matchRequestId: string; reason: string }; // Set when MATCHMAKING_V2_MATCH_END received
};

type SocialLinkProps = {
  platformId: string;
  link?: string;
  label: string;
};

// const ProfileTicket = () => (
//   <div className='flex items-center gap-2'>
//     <FontAwesomeIcon icon={faTicket} className='size-3 -rotate-[60deg] text-amber-600 lg:h-10 lg:w-8' />
//     <span className='text-sm text-[#FFFFFF] lg:text-lg'>x 2021</span>
//   </div>
// );

const SocialLink = ({ platformId, link, label }: SocialLinkProps) => {
  const platform = useMemo(
    () =>
      SOCIAL_PLATFORMS.find((p) => p.id === platformId) ||
      SOCIAL_PLATFORMS.find((p) => p.id === 'other') || { name: 'Other', icon: faLink, brandColor: '#FFFFFF' },
    [platformId],
  );

  if (!link) return null;

  console.log(platform);

  return (
    <Link to={link} target='_blank' rel='noopener noreferrer' className='flex items-center text-lg'>
      <FontAwesomeIcon icon={platform.icon} className='mr-2 size-3 md:size-4' style={{ color: platform.brandColor }} />
      <span className='text-stroke text-stroke-black ml-auto items-end text-xs font-semibold text-amber-400 lg:text-sm xl:text-lg'>
        {label || platform.name}
      </span>
    </Link>
  );
};

const LoadingSpinner = () => (
  <div className='flex h-full w-full items-center justify-center'>
    <FontAwesomeIcon className='size-10 animate-spin text-[#FFFFFF]' icon={faSpinner} />
  </div>
);

const ErrorMessage = ({ error }: { error: string }) => (
  <div className='flex h-full w-full flex-col items-center justify-center'>
    <h2 className='font-[Syne] text-2xl font-bold text-red-600'>Error Loading Profile</h2>
    <p className='text-sm text-[#FFFFFF]'>{error}</p>
  </div>
);

const NotFoundMessage = () => (
  <div className='flex h-full w-full items-center justify-center'>
    <h2 className='font-[Syne] text-2xl font-bold text-[#FFFFFF]'>Profile Not Found</h2>
  </div>
);

type Props = {
  authId: string;
  authToken: string | null;
  state: ProfileState | null;
  alias?: string;
  ownerAliases?: Partial<Record<IdentityPlatform, string>> | null;
  ownerUsernames?: Partial<Record<IdentityPlatform, string>> | null;
  ownedLobbies?: string[];
  joinStatus: PlayerJoinState['state'] | null;
  onEdit: (profile: Profile) => void;
  invitations?: Invitation[];
  invitationsLoading?: boolean;
  invitationsError?: string | null;
  onSettingsCogClick?: () => void;
  onPageForward?: () => void;
  onPageBack?: () => void;
  currentPage?: number;
  hasNextPage?: boolean;
  onSearch?: (term: string) => void;
  onCreateMatchmakerLobby?: () => void;
  onJoinMatchmakerLobby?: (playerName: string) => void;
  onLeaveMatchmakerLobby?: () => void;
  onStartMatchmakerLobby?: (useSteamGuestLogin?: boolean, regionOverride?: string) => void;
  onDestroyMatchmakerLobby: (selectedInvitationId?: string) => void;
  onSelectInvitation?: (invitationId?: string) => void;
  onKickPlayer?: (playerId: string) => void;
  invitationSendState: InvitationSendState;
  handleSendEmail: (invitationId: string, target: string, message: string) => void;
  handleSendSms: (invitationId: string, target: string, message: string) => void;
  handleTwitchBot: (invitationId: string) => void;
  handleDiscordBot: (invitationId: string) => void;
  onRemoveSelectedInvitation: () => void;
  onUpdatePlayerName: (name: string) => void;
  persistentId?: string;
  parentType?: string;
};

export const ProfileComponent = ({
  authId,
  authToken,
  state,
  alias,
  ownerAliases,
  ownerUsernames,
  ownedLobbies,
  joinStatus,
  onEdit,
  invitations = [],
  invitationsLoading = false,
  invitationsError = null,
  onSettingsCogClick,
  onPageForward,
  onPageBack,
  currentPage = 0,
  hasNextPage = false,
  onSearch,
  onCreateMatchmakerLobby,
  onJoinMatchmakerLobby,
  onLeaveMatchmakerLobby,
  onStartMatchmakerLobby,
  onDestroyMatchmakerLobby,
  onSelectInvitation,
  onKickPlayer,
  invitationSendState,
  handleSendEmail,
  handleSendSms,
  handleTwitchBot,
  handleDiscordBot,
  onRemoveSelectedInvitation,
  onUpdatePlayerName,
  persistentId,
  parentType,
}: Props) => {
  // State management
  const [editedProfile, setEditedProfile] = useState<Profile | null>(null);
  const [profileDisplayName, setProfileDisplayName] = useState<string>('Anonymous User');
  // const [showFollowersTab, setShowFollowersTab] = useState<boolean>(!isMobileDevice());
  const [showEditProfileBanner, setShowEditProfileBanner] = useState<boolean>(false);
  const [showEditAlias, setShowEditAlias] = useState<boolean>(false);
  const [showEditSocialLinks, setShowEditSocialLinks] = useState<boolean>(false);
  const [selectedBanner, setSelectedBanner] = useState<string>('/banners/banner1.png');
  const [socialLinkPlatform, setSocialLinkPlatform] = useState<string>('');
  const [socialLinkUrl, setSocialLinkUrl] = useState<string>('');
  const [urlError, setUrlError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchTimeoutRef] = useState(() => ({ current: undefined as NodeJS.Timeout | undefined }));
  const [isUserOwner, setIsUserOwner] = useState<boolean>(false);
  const [canCreateSessions, setCanCreateSessions] = useState<boolean>(false);
  const [bannerLoadError, setBannerLoadError] = useState<boolean>(false);
  const [isProfileEditing, setIsProfileEditing] = useState<boolean>(false);
  const [showLauncher, setShowLauncher] = useState(false);
  const selectedCardRef = useRef<HTMLDivElement>(null);
  const [launcherPosition, setLauncherPosition] = useState<{ x: number; y: number } | null>(null);
  const [pendingLauncherOpen, setPendingLauncherOpen] = useState(false);
  const [isPortrait, setIsPortrait] = useState(window.innerHeight > window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      setIsPortrait(screenHeight > screenWidth);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchTerm(value);

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        if (onSearch) {
          onSearch(value);
        }
      }, 1000);
    },
    [onSearch, searchTimeoutRef],
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTimeoutRef]);

  useEffect(() => {
    if (authId.startsWith('user_')) {
      setCanCreateSessions(true);
    } else {
      setCanCreateSessions(false);
    }
  }, [authId]);

  // URL validation effect
  useEffect(() => {
    if (!socialLinkUrl) {
      setUrlError('');
      return;
    }

    if (socialLinkPlatform === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(socialLinkUrl) && !socialLinkUrl.startsWith('mailto:')) {
        setUrlError('Please enter a valid email address');
      } else {
        setUrlError('');
      }
    } else {
      if (socialLinkUrl.trim() === '') {
        setUrlError('Please enter a valid URL');
      } else {
        setUrlError('');
      }
    }
  }, [socialLinkUrl, socialLinkPlatform]);

  useEffect(() => {
    setIsUserOwner(!!state?.isOwner);
  }, [state?.isOwner]);

  // Profile state effect
  useEffect(() => {
    if (!state) return;
    setEditedProfile(state.profile);
    const aliasDisplay = alias ? alias.charAt(0).toUpperCase() + alias.slice(1) : '';
    setProfileDisplayName(state.profile?.displayName || aliasDisplay || 'Anonymous');
  }, [state, alias]);

  // Banner state effect
  useEffect(() => {
    if (!state) return;
    setSelectedBanner(state.profile?.bannerUrl || '/banners/banner1.png');
    setBannerLoadError(false);
  }, [state]);

  // Reset banner error when selectedBanner changes
  useEffect(() => {
    setBannerLoadError(false);
  }, [selectedBanner]);

  // Event handlers
  const handleSocialLinkSubmit = useCallback(() => {
    if (!socialLinkPlatform || !editedProfile) return;

    const updatedProfile = {
      ...editedProfile,
      socialLinks: {
        ...editedProfile.socialLinks,
      },
    };

    if (!socialLinkUrl || urlError) {
      delete updatedProfile.socialLinks[socialLinkPlatform];
    } else {
      updatedProfile.socialLinks[socialLinkPlatform] = sanitizeUrl(socialLinkUrl, socialLinkPlatform);
    }

    setEditedProfile(updatedProfile);
    onEdit(updatedProfile);
    setSocialLinkPlatform('');
    setSocialLinkUrl('');
    setShowEditSocialLinks(false);
  }, [socialLinkPlatform, socialLinkUrl, urlError, editedProfile, onEdit]);

  const handleRemoveSocialLink = useCallback(() => {
    if (!socialLinkPlatform || !editedProfile) return;
    const updatedProfile = {
      ...editedProfile,
      socialLinks: {
        ...editedProfile.socialLinks,
      },
    };
    delete updatedProfile.socialLinks[socialLinkPlatform];
    setEditedProfile(updatedProfile);
    onEdit(updatedProfile);
  }, [socialLinkPlatform, editedProfile, onEdit]);

  const handleBannerSubmit = useCallback(() => {
    if (!editedProfile) return;
    const updatedProfile = {
      ...editedProfile,
      bannerUrl: selectedBanner,
    };
    setEditedProfile(updatedProfile);
    onEdit(updatedProfile);
    setShowEditProfileBanner(false);
  }, [editedProfile, selectedBanner, onEdit]);

  const handleAliasSubmit = useCallback(
    (platform: IdentityPlatform) => {
      if (!editedProfile) return;
      const updatedProfile = {
        ...editedProfile,
        selectedAliasPlatform: platform,
      };
      setEditedProfile(updatedProfile);
      onEdit(updatedProfile);
      setShowEditAlias(false);
      const newDisplayName = ownerUsernames?.[platform];
      if (newDisplayName) {
        setProfileDisplayName(newDisplayName);
      }
    },
    [editedProfile, onEdit, ownerUsernames],
  );

  const handleLauncherClick = useCallback(
    (invitationId: string) => {
      sendAnalytics({
        name: 'Lobby Card Opened',
        role: 'host',
        persistentId: persistentId,
        profileAlias: state?.profile?.displayName,
        hostAuthId: authId,
        playerAuthId: authId,
        lobbyId: invitationId,
      } as PlaycastAnalyticsLobbyCardOpened);

      onSelectInvitation?.(invitationId);
      setShowLauncher(false);
      setTimeout(() => {
        if (selectedCardRef.current) {
          const rect = selectedCardRef.current.getBoundingClientRect();
          setLauncherPosition({
            x: rect.right + 24, // 24px gap
            y: rect.top + rect.height / 2,
          });
        } else {
          setLauncherPosition(null);
        }
        setShowLauncher(true);
      }, 0);
    },
    [onSelectInvitation],
  );

  const handleShowLauncher = useCallback(() => {
    if (state?.selectedInvitationId) {
      setShowLauncher(true);
    }
  }, [state?.selectedInvitationId]);

  // Show launcher when selectedInvitationId changes
  useEffect(() => {
    handleShowLauncher();
  }, [handleShowLauncher]);

  // Add new function to get button text based on invitation state
  const getInvitationButtonText = useCallback(
    (invitation: Invitation) => {
      const players = (invitation as Invitation & { type: 'cafe' })?.manifest?.players || [];
      const isInQueue = state?.inQueue === invitation.id;
      const isMatchFound = state?.matchFoundId === invitation.id;
      const isMatchBroadcast = state?.matchBroadcastId === invitation.id;

      if (isInQueue) return 'Joining...';
      if (isMatchFound) return 'Match Found!';
      if (isMatchBroadcast) return 'Opening Lobby...';
      if (players.length >= 4) return 'Lobby Full';
      if (players.length > 0) return 'Join Lobby';
      return 'Launch';
    },
    [state?.inQueue, state?.matchFoundId, state?.matchBroadcastId],
  );

  // Add new function to get button icon based on invitation state
  const getInvitationButtonIcon = useCallback(
    (invitation: Invitation) => {
      const players = (invitation as Invitation & { type: 'cafe' })?.manifest?.players || [];
      const isInQueue = state?.inQueue === invitation.id;
      const isMatchFound = state?.matchFoundId === invitation.id;
      const isMatchBroadcast = state?.matchBroadcastId === invitation.id;

      if (isInQueue || isMatchFound || isMatchBroadcast) return faSpinner;
      if (players.length >= 4) return faUser;
      return faPlay;
    },
    [state?.inQueue, state?.matchFoundId, state?.matchBroadcastId],
  );

  // Add new function to check if button should be disabled
  const isInvitationButtonDisabled = useCallback(
    (invitation: Invitation) => {
      const players = (invitation as Invitation & { type: 'cafe' })?.manifest?.players || [];
      const isInQueue = state?.inQueue === invitation.id;
      const isMatchFound = state?.matchFoundId === invitation.id;
      const isMatchBroadcast = state?.matchBroadcastId === invitation.id;

      return players.length >= 4 || isInQueue || isMatchFound || isMatchBroadcast;
    },
    [state?.inQueue, state?.matchFoundId, state?.matchBroadcastId],
  );

  // const handleRedirectSteam = useCallback(() => {
  //   // steam://nav/games
  //   // Dont open in new tab because its a deep link.
  //   window.location.href = 'steam://nav/games';
  // }, []);

  const socialLinksFormatted = useMemo(() => {
    if (!state?.profile?.socialLinks) return [];
    return Object.entries(state.profile.socialLinks).map(([platform, url]) => ({
      platformId: platform,
      href: typeof url === 'string' ? sanitizeUrl(url, platform) : '',
      label: SOCIAL_PLATFORMS.find((p) => p.id === platform)?.name || platform,
    }));
  }, [state?.profile?.socialLinks]);

  // Memoize the player slots array to prevent unnecessary re-renders
  const playerSlots = useMemo(() => Array.from({ length: 4 }), []);

  // Add new function to check if user can create more lobbies
  const canCreateMoreLobbies = useCallback(() => {
    if (!LIMIT_LOBBY_CREATION) return true;
    const currentLobbyCount = ownedLobbies?.length || 0;
    return currentLobbyCount < MAX_OWNED_LOBBIES;
  }, [ownedLobbies]);

  // Add new function to get create button text based on lobby limits
  const getCreateButtonText = useCallback(() => {
    if (state?.creating) return 'Creating...';
    if (!canCreateMoreLobbies()) return `Lobby Limit Reached`;
    return 'Create Lobby';
  }, [state?.creating, canCreateMoreLobbies]);

  // Add new function to check if create button should be disabled
  const isCreateButtonDisabled = useCallback(() => {
    return !!state?.creating || !canCreateMoreLobbies();
  }, [state?.creating, canCreateMoreLobbies]);

  // Use either the state's invitations or the prop invitations
  const displayInvitations = useMemo(() => {
    if (!state) return [];
    const newInvitations = state.invitations || invitations;

    // Filter out invitations that have a soft kick expiration
    const filteredInvitations = newInvitations.filter((invitation) => {
      // Check if the invitation has a soft kick expiration time
      const hasSoftKickExpiration = invitation.type === 'cafe' &&
        invitation.manifest?.softKickExpirationTime;

      // Only include invitations that do NOT have a soft kick expiration
      return !hasSoftKickExpiration;
    });

    // If there's a selected invitation, move it to the front of the array
    if (state.selectedInvitationId) {
      const selectedIndex = filteredInvitations.findIndex((inv) => inv.id === state.selectedInvitationId);
      if (selectedIndex > 0) {
        const reorderedInvitations = [...filteredInvitations];
        const [selectedInvitation] = reorderedInvitations.splice(selectedIndex, 1);
        reorderedInvitations.unshift(selectedInvitation);
        return reorderedInvitations;
      }
    }

    return filteredInvitations;
  }, [invitations, state]);

  // Replace direct onCreateMatchmakerLobby usage with this handler
  const handleCreateMatchmakerLobby = useCallback(() => {
    // Lobby Creation Init
    sendAnalytics({
      name: 'Lobby Creation Initialized',
      role: 'host',
      persistentId: persistentId,
      action: 'create lobby button',
      hostAuthId: authId,
      profileAlias: state?.profile?.displayName,
      source: parentType,
    } as PlaycastAnalyticsLobbyCreationInitialized);

    onCreateMatchmakerLobby?.();
    setPendingLauncherOpen(true);
  }, [onCreateMatchmakerLobby]);

  // Reusable function to calculate launcher position
  const calculateLauncherPosition = useCallback((cardElement: HTMLDivElement | null) => {
    if (!cardElement) return null;

    const GAP = 24;
    const OFFSET = 96;
    const rect = cardElement.getBoundingClientRect();
    const modalWidth = 420; // fallback width, should match modal's max width
    const modalHeight = 600; // fallback height, should match modal's max height
    const isMobile = window.innerWidth <= 768;

    let x, y;
    if (isMobile) {
      x = (window.innerWidth - modalWidth) / 2;
      y = (window.innerHeight - modalHeight) / 2;
    } else {
      x = rect.right + GAP;
      y = rect.top + rect.height / 2 - modalHeight / 2 + OFFSET;
      if (x + modalWidth > window.innerWidth) {
        x = rect.left - modalWidth - GAP;
      }
      x = Math.max(0, Math.min(x, window.innerWidth - modalWidth));
      y = Math.max(0, Math.min(y, window.innerHeight - modalHeight));
    }

    return { x, y };
  }, []);

  const handleDestroyLobbyClick = useCallback(
    (invitationId: string) => {
      onDestroyMatchmakerLobby(invitationId);
      setShowLauncher(false);
    },
    [onDestroyMatchmakerLobby],
  );

  // Effect to open launcher after card is rendered
  useEffect(() => {
    if (pendingLauncherOpen && showLauncher === false && selectedCardRef.current) {
      const position = calculateLauncherPosition(selectedCardRef.current);
      setLauncherPosition(position);
      setShowLauncher(true);
      setPendingLauncherOpen(false);
    }
  }, [pendingLauncherOpen, showLauncher, calculateLauncherPosition]);

  // Effect to update position when selected invitation changes
  useEffect(() => {
    if (state?.selectedInvitationId && selectedCardRef.current && showLauncher) {
      const position = calculateLauncherPosition(selectedCardRef.current);
      setLauncherPosition(position);
    }
  }, [state?.selectedInvitationId, showLauncher, calculateLauncherPosition]);

  if (!state) return null;
  if (state.isLoading) return <LoadingSpinner />;
  if (state.error) return <ErrorMessage error={state.error} />;
  if (!state.profile) return <NotFoundMessage />;

  const { profile } = state;

  // Use either the state's invitations or the prop invitations
  const isInvitationsLoading = state.invitationsLoading || invitationsLoading;
  const invitationsErrorMessage = state.invitationsError || invitationsError;

  // console.log('########################', isUserOwner);

  return (
    <div className='relative flex h-svh flex-1 overflow-hidden text-[#FFFFFF]'>
      <style>{hideScrollbarStyles}</style>
      <div className='flex flex-1 flex-col overflow-hidden'>
        {/* <div
          className='relative flex size-full max-h-[15%] flex-col gap-4 bg-cover bg-center bg-no-repeat p-2 lg:max-h-[20%] lg:flex-row lg:px-8 lg:py-6'
          style={{
            backgroundImage: `url(${bannerLoadError ? '/banners/banner1.png' : isUserOwner ? selectedBanner : state.profile?.bannerUrl || '/banners/banner1.png'})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
          }}
        > */}
        <div
          className={clsx(
            'relative flex size-full max-h-[30%] gap-4 p-2 lg:max-h-[28%] lg:px-8 lg:py-6',
            isMobileDevice() && (isPortrait ? 'max-h-[16%]' : 'max-h-[25%]'),
          )}
          style={{
            backgroundImage: `url(${bannerLoadError ? '/banners/banner1.png' : isUserOwner ? selectedBanner : state.profile?.bannerUrl || '/banners/banner1.png'})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
          }}
        >
          <div className='absolute left-2 top-2 z-20 flex items-center justify-end gap-2 text-right md:right-6 md:top-2 md:gap-4 lg:top-6'>
            {isUserOwner && (
              <>
                <button onClick={() => setIsProfileEditing(!isProfileEditing)} className='group flex items-center gap-2'>
                  <FontAwesomeIcon
                    icon={isProfileEditing ? faCheck : faPencil}
                    className='size-3 text-[#FFFFFF] group-hover:opacity-80 md:size-4'
                    strokeWidth='15'
                    stroke='black'
                  />
                  <span className='text-stroke text-stroke-black text-xs font-semibold text-[#FFFFFF] group-hover:opacity-80 md:text-base'>
                    {isProfileEditing ? 'Done Editing' : 'Edit Page'}
                  </span>
                </button>
                {!isProfileEditing && (
                  <FontAwesomeIcon
                    icon={faGear}
                    className='size-4 cursor-pointer text-[#FFFFFF] transition-colors hover:text-gray-300 md:size-5'
                    onClick={onSettingsCogClick}
                    strokeWidth='15'
                    stroke='black'
                    data-testid='settings-cog-button'
                  />
                )}
              </>
            )}
          </div>
          {/* Hidden image to detect loading errors */}
          <img
            src={isUserOwner ? selectedBanner : state.profile?.bannerUrl || '/banners/banner1.png'}
            alt='Banner'
            className='hidden'
            onError={() => setBannerLoadError(true)}
            onLoad={() => setBannerLoadError(false)}
          />
          {/* <div className='absolute inset-0 z-0 bg-black/45 backdrop-invert backdrop-opacity-10' /> */}
          <div className='z-10 flex flex-1 items-center gap-2 lg:gap-4'>
            <img
              className='size-8 rounded-full md:size-16 lg:size-36'
              src={profile.avatarUrl || '/default-avatar.png'}
              alt={profileDisplayName || 'Profile'}
            />
            <div className='relative'>
              <h2 className='w-full max-w-[12rem] pr-10 font-[Inter] text-2xl font-semibold text-[#A684FF] drop-shadow-[3px_3px_1px_#5204AA] md:max-w-md md:text-3xl lg:max-w-2xl lg:text-5xl'>
                <span className='block truncate leading-[5rem]'>{profileDisplayName}</span>
              </h2>
              {isUserOwner && isProfileEditing && (
                <TooltipProvider>
                  <Tooltip delayDuration={300}>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => setShowEditAlias(true)}
                        className='absolute right-2 top-1/2 -translate-y-1/2'
                        data-testid='edit-alias-button'
                      >
                        <FontAwesomeIcon icon={faPencil} className='size-4 text-[#FFFFFF]' strokeWidth='15' stroke='black' />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Edit Alias</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
          {/* <div className='z-10 flex flex-col'>
            <div className='flex-1' />
            <div className='relative flex flex-col gap-0 text-sm'>
              <div className='absolute -left-6 -top-4 z-10 hidden lg:flex'>
                <button onClick={() => setShowEditSocialLinks(true)}>
                  <FontAwesomeIcon icon={faPencil} className='size-4 text-[#FFFFFF]' flip='horizontal' />
                </button>
              </div>
              {socialLinksFormatted.map((link, index) => (
                <SocialLink key={index} platformId={link.platformId} link={link.href} label={link.label} />
              ))}
            </div>
          </div> */}
          <div className='absolute bottom-2 right-2 z-10 flex flex-col md:right-6'>
            <div className='relative flex flex-col gap-0 text-sm'>
              <div
                className={clsx(
                  'absolute -left-6 z-10 sm:-left-6',
                  socialLinksFormatted.length > 0 ? '-top-1 sm:-top-4' : '-top-5 sm:-top-10',
                )}
              >
                {isUserOwner && isProfileEditing && (
                  <TooltipProvider>
                    <Tooltip delayDuration={300}>
                      <TooltipTrigger asChild>
                        <button onClick={() => setShowEditSocialLinks(true)}>
                          <FontAwesomeIcon icon={faPencil} className='size-3 text-[#FFFFFF] md:size-4' flip='horizontal' />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Edit Social Links</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              {socialLinksFormatted.map((link, index) => (
                <SocialLink key={index} platformId={link.platformId} link={link.href} label={link.label} />
              ))}
            </div>
          </div>
          <div className='absolute bottom-2 left-2 z-20 md:left-6 md:top-6'>
            {isUserOwner && isProfileEditing && (
              <TooltipProvider>
                <Tooltip delayDuration={300}>
                  <TooltipTrigger asChild>
                    <button onClick={() => setShowEditProfileBanner(true)} data-testid='edit-banner-button'>
                      <FontAwesomeIcon icon={faPencil} className='size-3 text-[#FFFFFF] md:size-4' flip='horizontal' />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Edit Banner Image</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
        <div
          className='hide-scrollbar flex flex-1 flex-col gap-4 overflow-y-auto bg-gradient-to-b from-[#416188] to-[#121726] p-2 md:p-4 md:px-16'
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {/* <div className='mt-6 flex justify-end md:mt-0'>
            <Input
              type='text'
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder='Search Lobbies...'
              className='w-full rounded-full bg-[#638BBD] pr-8 text-[#A5C6F1] placeholder:text-[#A5C6F1] sm:w-fit'
              data-testid='invitation-search-input'
              style={{
                boxShadow: '-2px -2px 4px rgba(127, 174, 234, 0.5) inset, 2px 2px 4px rgba(0, 0, 0, 0.25) inset',
              }}
            />
          </div> */}
          {isInvitationsLoading && (
            <div className='flex h-32 items-center justify-center'>
              <FontAwesomeIcon className='size-8 animate-spin text-gray-100' icon={faSpinner} />
            </div>
          )}

          {!isInvitationsLoading && !invitationsErrorMessage && (
            <div className='flex flex-col gap-4'>
              {displayInvitations.length === 0 && (
                <div className='flex h-full w-full justify-center pt-7 md:pt-0'>
                  <h2 className='font-[Syne] text-lg font-semibold text-[#FFFFFF]'>No Lobbies found</h2>
                </div>
              )}
              {/* Game card grid with mobile padding */}
              <div
                className={[
                  'grid',
                  'gap-5 md:gap-16', // 1.25rem gap on mobile, 4rem on md+
                  isMobileDevice()
                    ? isPortrait
                      ? 'grid-cols-2'
                      : 'grid-cols-4'
                    : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 lg:gap-14 xl:grid-cols-4 xl:gap-16 2xl:grid-cols-6 2xl:gap-16',
                  'p-5 md:p-0',
                ].join(' ')}
              >
                <SignedOut>
                  <SignInButton mode='modal'>
                    <button
                      className='relative flex aspect-[5/6] w-full flex-col items-center justify-center transition-transform duration-100 hover:-translate-x-0.5 hover:-translate-y-0.5 hover:opacity-80'
                      style={{
                        boxShadow: '8px 8px 4px 0px rgba(0, 0, 0, 0.35)',
                        border: '5.5px dashed #f59e0b',
                        borderStyle: 'dashed',
                        borderWidth: '5.5px',
                        borderColor: '#f59e0b',
                        boxSizing: 'border-box',
                      }}
                      data-testid='login-button'
                    >
                      <div className='relative flex size-full flex-col items-center justify-center overflow-hidden text-center'>
                        <span
                          className='mb-2 w-24 whitespace-normal break-words text-center font-[Inter] text-sm sm:w-28 sm:text-base md:w-32 md:text-lg lg:w-36 lg:text-xl xl:w-40 xl:text-2xl text-amber-400'
                          style={{ textShadow: '4px 4px 4px rgba(0, 0, 0, 0.50), 0px 0px 3px #FFDD89' }}
                        >
                          Create Lobby
                        </span>
                        <FontAwesomeIcon
                          icon={faPlus}
                          className='size-12 sm:size-14 md:size-16 lg:size-20 xl:size-24 text-amber-400'
                          filter='drop-shadow(0px 0px 8px #FDBE25) drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.25)) drop-shadow(6px 6px 4px rgba(0, 0, 0, 0.40))'
                        />
                      </div>
                    </button>
                  </SignInButton>
                </SignedOut>
                {canCreateSessions && (
                  <button
                    className={`relative flex aspect-[5/6] w-full flex-col items-center justify-center transition-transform duration-100 hover:-translate-x-0.5 hover:-translate-y-0.5 ${
                      isCreateButtonDisabled() ? 'cursor-not-allowed opacity-60' : 'hover:opacity-80'
                    }`}
                    style={{
                      boxShadow: '8px 8px 4px 0px rgba(0, 0, 0, 0.35)',
                      border: '5.5px dashed #f59e0b', // Explicit border style
                      borderStyle: 'dashed',
                      borderWidth: '5.5px',
                      borderColor: '#f59e0b',
                      boxSizing: 'border-box',
                    }}
                    onClick={handleCreateMatchmakerLobby}
                    disabled={isCreateButtonDisabled()}
                    data-testid='create-session-button'
                  >
                    <div className='relative flex size-full flex-col items-center justify-center overflow-hidden text-center'>
                      {state?.creating ? (
                        <>
                          <span
                            className='mb-2 font-[Inter] text-lg text-amber-400 sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl'
                            style={{ textShadow: '4px 4px 4px rgba(0, 0, 0, 0.50), 0px 0px 3px #FFDD89' }}
                          >
                            Creating...
                          </span>
                          <FontAwesomeIcon
                            icon={faSpinner}
                            className='size-12 animate-spin text-amber-400 sm:size-14 md:size-16 lg:size-20 xl:size-24'
                          />
                        </>
                      ) : (
                        <>
                          <span
                            className={`mb-2 w-24 whitespace-normal break-words text-center font-[Inter] text-sm sm:w-28 sm:text-base md:w-32 md:text-lg lg:w-36 lg:text-xl xl:w-40 xl:text-2xl ${isCreateButtonDisabled() ? 'text-gray-400' : 'text-amber-400'}`}
                            style={{ textShadow: '4px 4px 4px rgba(0, 0, 0, 0.50), 0px 0px 3px #FFDD89' }}
                          >
                            {getCreateButtonText()}
                          </span>
                          <FontAwesomeIcon
                            icon={faPlus}
                            className={`size-12 sm:size-14 md:size-16 lg:size-20 xl:size-24 ${isCreateButtonDisabled() ? 'text-gray-400' : 'text-amber-400'}`}
                            filter='drop-shadow(0px 0px 8px #FDBE25) drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.25)) drop-shadow(6px 6px 4px rgba(0, 0, 0, 0.40))'
                          />
                        </>
                      )}
                    </div>
                  </button>
                )}
                {displayInvitations.map((invitation) => {
                  const isSelected = state.selectedInvitationId === invitation.id;
                  const players = (invitation as Invitation & { type: 'cafe' })?.manifest?.players || [];
                  const isUserLobbyOwner = invitation.owner.profileId === authId;

                  return (
                    <div
                      key={invitation.id}
                      ref={isSelected ? selectedCardRef : undefined}
                      className={clsx(
                        'bg-darkest relative flex aspect-[5/6] w-full flex-col border-[5.5px]',
                        isSelected ? 'border-amber-400' : 'border-[#1A273F]',
                      )}
                      style={{ boxShadow: '8px 8px 4px 0px rgba(0, 0, 0, 0.35)' }}
                    >
                      {isUserLobbyOwner && (
                        <button
                          className='absolute -right-3 -top-3 z-50 flex items-center rounded-full bg-red-900 p-2 shadow-[inset_3px_3px_2px_0px_#DC2626,_inset_-3px_-3px_2px_0px_#0B0C0F,_3px_3px_3px_0px_#0B0C0F]'
                          onClick={() => handleDestroyLobbyClick(invitation.id)}
                        >
                          <FontAwesomeIcon icon={faXmark} className='size-5 text-[#FFFFFF]' />
                        </button>
                      )}
                      {/* <img
                          src={`/steam/steam${Math.floor(Math.random() * 5) + 1}.png`}
                          alt='Steam'
                          className='size-full bg-cover bg-center'
                        /> */}
                      <div className='relative flex size-full flex-col items-center justify-center overflow-hidden text-center'>
                        <img
                          src={
                            invitation.type === 'cafe' && invitation.manifest?.steamId
                              ? `https://cdn.akamai.steamstatic.com/steam/apps/${invitation.manifest.steamId}/library_600x900.jpg`
                              : '/icon.png'
                          }
                          alt='Steam'
                          className='w-full object-cover'
                          onError={(e) => {
                            // Fallback to default icon if Steam poster fails to load
                            (e.target as HTMLImageElement).src = '/icon.png';
                          }}
                        />
                        <div className='bg-darkest/40 absolute inset-0 flex items-center justify-center' />
                        {!(invitation.type === 'cafe' && invitation.manifest?.steamId) && (
                          <div className='absolute left-1/2 top-1/2 flex w-full -translate-x-1/2 -translate-y-1/2 font-[Syne] text-2xl font-bold text-[#FFFFFF]'>
                            <span className='mx-auto overflow-hidden text-ellipsis px-4 text-center'>
                              {invitation.name || 'Untitled Invitation'}
                            </span>
                          </div>
                        )}
                      </div>
                      <button
                        className='absolute inset-0 flex cursor-pointer flex-col items-center justify-center gap-4 bg-black/80 px-3 opacity-0 transition-opacity duration-200 hover:opacity-100'
                        onClick={() => handleLauncherClick(invitation.id)}
                        data-testid={`invitation-${invitation.id}-button`}
                      >
                        {invitation.type === 'cafe' && invitation.manifest?.steamId && (
                          <div className='flex w-full font-[Inter] text-lg font-bold text-[#FFFFFF]'>
                            <span className='mx-auto overflow-hidden text-ellipsis px-4 text-center'>
                              {invitation.name || 'Untitled Invitation'}
                            </span>
                          </div>
                        )}
                        <FontAwesomeIcon icon={faPlay} className='size-16 text-amber-400' />
                      </button>
                      <div className='bg-darker/30 absolute bottom-0 right-0 flex w-8/12 items-center gap-0.5 px-1 py-2'>
                        {playerSlots.map((_, index) => {
                          const isPlayer = index < players.length;
                          return (
                            <div
                              key={index}
                              className={`relative flex aspect-square w-full flex-1 items-center justify-center overflow-hidden rounded-full border-2 lg:border-4 ${
                                isPlayer ? 'border-[#00681F] bg-[#00FF4D]' : 'border-[#626262] bg-[#C9C9C9]'
                              }`}
                            >
                              {isPlayer && <FontAwesomeIcon icon={faUser} className='h-full w-full text-[#00681F]' />}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {displayInvitations.length > 0 && (
                <>
                  {/* Pagination controls */}
                  {(onPageBack || onPageForward) && (
                    <div className='flex items-center justify-center gap-4'>
                      <button
                        onClick={onPageBack}
                        disabled={currentPage <= 0}
                        className='flex items-center gap-1 text-amber-400 hover:text-amber-400/80 disabled:text-gray-400 disabled:opacity-50'
                        data-testid='pagination-prev-button'
                      >
                        <FontAwesomeIcon icon={faChevronLeft} className='size-3' />
                        Previous
                      </button>
                      <span className='text-sm text-[#FFFFFF]'>Page {currentPage + 1}</span>
                      <button
                        onClick={onPageForward}
                        disabled={!hasNextPage}
                        className='flex items-center gap-1 text-amber-400 hover:text-amber-400/80 disabled:text-gray-400 disabled:opacity-50'
                        data-testid='pagination-next-button'
                      >
                        Next
                        <FontAwesomeIcon icon={faChevronRight} className='size-3' />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Followers Tab */}
      {/* <div
        className={clsx(
          'bg-darkest absolute right-0 z-50 flex h-full transition-all duration-500 lg:relative',
          showFollowersTab ? 'translate-x-0 lg:w-60 lg:translate-x-0' : 'translate-x-full lg:w-0 lg:translate-x-0',
        )}
      >
        <div className='flex w-60 min-w-60 flex-col gap-2 overflow-hidden'>
          <div className='flex flex-col gap-2 px-4 pt-2 lg:pt-6'>
            <button onClick={() => setShowFollowersTab(false)} className='flex'>
              <FontAwesomeIcon icon={faArrowRight} className='size-5 text-[#FFFFFF]' />
            </button>
            <h3 className='font-[Syne] text-2xl'>Followers</h3>
            <input type='text' placeholder='Search' className='text-darkest h-8 w-full items-center rounded-full text-sm' />
          </div>
          <div className='flex flex-col gap-2 overflow-y-auto pb-2 pl-4'>
            {Array.from({ length: 20 }).map((_, index) => (
              <div key={index} className='flex items-center gap-2'>
                <img
                  className='size-10 rounded-full'
                  src={profile.avatarUrl || '/default-avatar.png'}
                  alt={profileDisplayName || 'Profile'}
                />
                <span className='text-sm'>John Doe</span>
              </div>
            ))}
          </div>
        </div>
      </div> */}
      <BannerSelectionModal
        authToken={authToken}
        isOpen={showEditProfileBanner}
        onClose={() => setShowEditProfileBanner(false)}
        onBannerSubmit={(bannerUrl) => {
          if (!editedProfile) return;
          const updatedProfile = {
            ...editedProfile,
            bannerUrl: bannerUrl,
          };
          setEditedProfile(updatedProfile);
          onEdit(updatedProfile);
          setSelectedBanner(bannerUrl);
          setShowEditProfileBanner(false);
        }}
      />
      <AliasSelectionModal
        isOpen={showEditAlias}
        aliases={ownerUsernames}
        defaultPlatform={state.profile.selectedAliasPlatform}
        onClose={() => setShowEditAlias(false)}
        onApply={(platform) => {
          handleAliasSubmit(platform);
        }}
      />
      <SocialLinksModal
        isOpen={showEditSocialLinks}
        onClose={() => setShowEditSocialLinks(false)}
        socialLinkPlatformState={[socialLinkPlatform, setSocialLinkPlatform]}
        socialLinkUrlState={[socialLinkUrl, setSocialLinkUrl]}
        handleSocialLinkSubmit={handleSocialLinkSubmit}
        handleRemoveSocialLink={handleRemoveSocialLink}
        editedProfile={editedProfile}
      />
      <LauncherModal
        onClose={() => {
          onLeaveMatchmakerLobby && onLeaveMatchmakerLobby();
          onSelectInvitation && onSelectInvitation(undefined);
          setShowLauncher(false);
        }}
        onDestroy={onDestroyMatchmakerLobby}
        authId={authId}
        ownerAliases={ownerAliases}
        selectedInvitationId={state.selectedInvitationId}
        invitations={displayInvitations}
        onJoin={onJoinMatchmakerLobby}
        onLeave={onLeaveMatchmakerLobby}
        onStart={onStartMatchmakerLobby}
        onKickPlayer={onKickPlayer}
        inQueue={state.inQueue}
        matchFoundId={state.matchFoundId}
        matchBroadcastId={state.matchBroadcastId}
        handleSendEmail={handleSendEmail}
        handleSendSms={handleSendSms}
        invitationSendState={invitationSendState}
        handleTwitchBot={handleTwitchBot}
        handleDiscordBot={handleDiscordBot}
        isVisible={showLauncher}
        onRemoveSelectedInvitation={onRemoveSelectedInvitation}
        initialPosition={launcherPosition}
        onUpdatePlayerName={onUpdatePlayerName}
        persistentId={persistentId}
        parentType={parentType}
      />
    </div>
  );
};
